import React, { useState, useEffect, useRef } from 'react';
import * as api from '../services/api';
import { X, Loader, Copy, Check, FileText } from 'lucide-react';

// ── Shared helpers (mirrors LogList / Analytics) ──────────────────────────────

const LevelBadge = ({ level }) => (
  <span className={`badge level-${level?.toLowerCase()}`}>{level}</span>
);

const formatRelativeTime = (timestamp) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const formatBytes = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

// ── Component ──────────────────────────────────────────────────────────────────

const LogBodyDetailModal = ({ log, workerApiUrl, onClose }) => {
  const [bodyJson, setBodyJson] = useState(null);   // pretty-printed string for display
  const [sizeBytes, setSizeBytes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  // ── Close on Escape ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // ── Fetch body ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!log?.id) return;
    setLoading(true);
    setError(null);
    setBodyJson(null);

    api.getLogBody(workerApiUrl, log.id)
      .then(data => {
        // Prefer original_body (the raw data the developer sent) over the
        // server-side formatted version which splits it into Fields / Arrays / Nested.
        const raw = data.original_body ?? data.body;
        setBodyJson(JSON.stringify(raw, null, 2));
        setSizeBytes(data.size_bytes);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [log, workerApiUrl]);

  // ── Cleanup copy timer on unmount ────────────────────────────────────────────
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  // ── Copy to clipboard ────────────────────────────────────────────────────────
  const handleCopy = async () => {
    if (!bodyJson) return;
    try {
      await navigator.clipboard.writeText(bodyJson);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = bodyJson;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  // ── Click outside to close ───────────────────────────────────────────────────
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!log) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      {/* Use modal-content.large — the correct CSS class with flex layout */}
      <div className="modal-content large">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="modal-header">
          <div className="modal-title-group">
            <FileText size={18} />
            <h2>Log Body</h2>
          </div>
          <div className="modal-header-actions">
            {bodyJson && (
              <button
                className={`btn-secondary btn-small ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
                title="Copy JSON to clipboard"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                <span className="btn-text">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            )}
            <button className="modal-close" onClick={onClose} title="Close  (Esc)">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ── Metadata strip ──────────────────────────────────────────────── */}
        <div className="modal-meta-strip">
          <LevelBadge level={log.level} />
          <span
            className="modal-meta-time"
            title={new Date(log.timestamp).toLocaleString()}
          >
            {formatRelativeTime(log.timestamp)}
          </span>
          {log.template_name && (
            <span className="modal-meta-template">{log.template_name}</span>
          )}
          {sizeBytes && (
            <span className="modal-meta-size">{formatBytes(sizeBytes)}</span>
          )}
        </div>

        {/* ── Message strip (if the log has a message) ────────────────────── */}
        {log.message && (
          <div className="modal-log-message">
            {log.message}
          </div>
        )}

        {/* ── JSON body ───────────────────────────────────────────────────── */}
        <div className="json-viewer-container">
          {loading ? (
            <div className="modal-loading">
              <Loader size={20} className="spin" />
              <span>Loading body…</span>
            </div>
          ) : error ? (
            <div className="modal-error">
              Failed to load body: {error}
            </div>
          ) : bodyJson ? (
            <pre className="json-viewer">{bodyJson}</pre>
          ) : (
            <p className="modal-empty">No body content available.</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default LogBodyDetailModal;
